import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router'
import { ROUTES } from '../../routes/routeConfig'
import {
  HomeIcon,
  ReturnIcon,
  BookIcon,
  UserGroupIcon,
  HelpIcon,
  ChevronIcon,
  MenuIcon,
  CloseIcon,
  DownloadSidebarIcon,
  PremiumIcon,
  SelfServiceIcon,
} from '../../assets/icons'
import { useSelector, useDispatch } from "react-redux"
import { PREPARER, IT_ADMIN, ADMIN } from '../../utils/role-capabilities.js'
import { checkSelfServiceAccess } from '../../store/slice/selfServiceSlice'


const Sidebar = ({
  isCollapsed,
  setIsCollapsed,
  isMobileOpen,
  setIsMobileOpen,
  userRole,
  exitFullScreen
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const [helpCenterOpen, setHelpCenterOpen] = useState(false)
  const roleTypes = useSelector(state => state.user.role)
  const selfServiceEnabled = useSelector(state => state.selfService.enabled)

  // Check if Self Service is available
  useEffect(() => {
    dispatch(checkSelfServiceAccess())
  }, [])

  // Auto-expand Help Center if user is on a help page
  // Close Help Center if user navigates away from help pages (unless manually opened)
  useEffect(() => {
    if (location.pathname.startsWith('/help')) {
      setHelpCenterOpen(true)
    } else {
      // Only close if we're not manually keeping it open
      // This will be handled by the navigation logic
    }
  }, [location.pathname])

  // Menu items configuration based on user role
  const getMenuItems = userRole => {

    const baseItems = [
      {
        id: 'home',
        label: 'Home',
        icon: HomeIcon,
        path: ROUTES.DASHBOARD,
        roles: [IT_ADMIN, ADMIN, PREPARER],
      },
      {
        id: 'returns',
        label: 'Returns',
        icon: ReturnIcon,
        path: ROUTES.RETURNS,
        roles: [IT_ADMIN, ADMIN, PREPARER],
      },
      {
        id: 'provision',
        label: 'Provision & Advance knock off',
        icon: BookIcon,
        path: ROUTES.PROVISION,
        roles: [IT_ADMIN, ADMIN, PREPARER],
      },
    ]

    const adminItems = [
      {
        id: 'admin',
        label: 'Admin',
        icon: UserGroupIcon,
        path: ROUTES.ADMIN,
        roles: [IT_ADMIN, ADMIN, PREPARER],
      },
    ]

    let helpItems = [
      {
        id: 'premium',
        label: 'Premium Feature',
        icon: PremiumIcon,
        path: ROUTES.PREMIUM_FEATURE,
        roles: [IT_ADMIN],
      },
      {
        id: 'download',
        label: 'Downloads',
        icon: DownloadSidebarIcon,
        path: ROUTES.DOWNLOAD,
        roles: [IT_ADMIN, ADMIN, PREPARER],
      },
      // {
      //   id: 'help-center',
      //   label: 'Help Center',
      //   icon: HelpIcon,
      //   roles: [IT_ADMIN, ADMIN, PREPARER],
      //   isExpandable: true,
      //   subItems: [
      //     {
      //       id: 'user-manual',
      //       label: 'User Manual',
      //       path: ROUTES.HELP_USER_MANUAL,
      //     },
      //     {
      //       id: 'cof',
      //       label: 'COF',
      //       path: ROUTES.HELP_COF,
      //     },
      //     {
      //       id: 'download-templates',
      //       label: 'Download Templates',
      //       path: ROUTES.HELP_DOWNLOAD_TEMPLATES,
      //     },
      //   ],
      // },
    ]

    if (selfServiceEnabled) {
      helpItems = [
        ...helpItems,
        {
          id: 'self-service',
          label: 'Self Service Reports',
          icon: SelfServiceIcon,
          path: ROUTES.SELF_SERVICE,
          roles: [IT_ADMIN, ADMIN, PREPARER],
        },
      ]
    }

    let menuItems = [...baseItems]

    // Add help items for all users
    menuItems = [...menuItems, ...helpItems]

    // Add admin items if user is admin
    if (userRole === IT_ADMIN || userRole === ADMIN || userRole === PREPARER) {
      menuItems = [...menuItems, ...adminItems]
    }

    // Filter items based on user role
    return menuItems.filter(item => item.roles.includes(roleTypes))
  }

  const menuItems = getMenuItems(roleTypes)
  const handleNavigation = path => {
    if (path) {
      navigate(path)
      setIsCollapsed(true)
      exitFullScreen()
      // Close Help Center if navigating to a non-help page
      if (!path.startsWith('/help')) {
        setHelpCenterOpen(false)
      }
    }
    // Close mobile menu after navigation
    if (isMobileOpen) {
      setIsMobileOpen(false)
    }
  }

  const toggleHelpCenter = () => {
    setHelpCenterOpen(!helpCenterOpen)
    // Only expand sidebar if it's collapsed and we're opening help center
    if (isCollapsed && !helpCenterOpen) {
      setIsCollapsed(false)
    }
  }

  const isActive = path => {
    // If Help Center is open (manually clicked) and we're not on a help page,
    // don't highlight other menu items
    if (helpCenterOpen && !location.pathname.startsWith('/help')) {
      return false
    }

    // For admin routes, also consider sub-routes like /admin/add-tan, /admin/edit-tan
    if (path === ROUTES.ADMIN) {
      return location.pathname.startsWith('/admin')
    }

    return location.pathname === path
  }

  const isHelpCenterActive = () => {
    // Help Center is active if we're on a help page OR if it's manually opened
    return location.pathname.startsWith('/help') || helpCenterOpen
  }


  const getSidebarContent = (isMobile = false) => (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex  flex-col p-5 flex-shrink-0">
        {/* Desktop toggle button */}
        <button
          onClick={() => {
            setIsCollapsed(!isCollapsed)
            // Close Help Center when collapsing/expanding sidebar if not on a help page
            if (!location.pathname.startsWith('/help')) {
              setHelpCenterOpen(false)
            }
          }}
          className={`hidden lg:block ${isCollapsed ? 'mx-auto' : ''}`}
        >
          <MenuIcon className="h-5 w-5 text-gray-600" />
        </button>

        {/* Mobile close button */}
        <button
          onClick={() => setIsMobileOpen(false)}
          className="lg:hidden p-1 rounded-md cursor-pointer"
        >
          <CloseIcon className="h-5 w-5 text-gray-600" />
        </button>
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 overflow-y-auto px-1 py-6 hide-scrollbar space-y-[8px]">
        {menuItems.map(item => (
          <div key={item.id}>
            {item.isExpandable ? (
              // Help Center with submenu
              <div className="relative">
                <button
                  onClick={toggleHelpCenter}
                  className={`w-full flex items-center justify-between  ${helpCenterOpen && 'rounded-t-lg'} text-left transition-colors overflow-hidden ${helpCenterOpen || isHelpCenterActive()
                    ? 'bg-[var(--bg-brand-layer-2)] text-[var(--forbrand-primary)]'
                    : 'text-gray-700 hover:bg-gray-100'
                    }`}
                >
                  <div className="flex items-center justify-start">
                    <item.icon
                      className={`w-[48px] h-[48px] flex-shrink-0 ${!isMobile && isCollapsed ? '' : 'mr-3'
                        }`}
                      isSelected={helpCenterOpen || isHelpCenterActive()}
                    />
                    {(isMobile || !isCollapsed) && (
                      <span className="text-sm font-medium whitespace-nowrap">{item.label}</span>
                    )}
                  </div>
                  {(isMobile || !isCollapsed) && (
                    <div className="ml-auto">
                      {helpCenterOpen || isHelpCenterActive() ? (
                        <ChevronIcon direction="up" isSelected={true} />
                      ) : (
                        <ChevronIcon />
                      )}
                    </div>
                  )}
                </button>

                {/* Submenu */}
                {(helpCenterOpen || isHelpCenterActive()) &&
                  (isMobile || !isCollapsed) && (
                    <div className="absolute space-y-1 bg-[var(--bg-brand-layer-2)] rounded-b-lg p-2">
                      {item.subItems.map(subItem => (
                        <button
                          key={subItem.id}
                          onClick={() => handleNavigation(subItem.path)}
                          className={`w-full text-left px-3 py-1 text-sm rounded-md transition-colors ${isActive(subItem.path)
                            ? 'text-[var(--forbrand-primary)] font-medium'
                            : 'text-[var(--forbrand-primary)]'
                            }`}
                        >
                          {subItem.label}
                        </button>
                      ))}
                    </div>
                  )}
              </div>
            ) : (
              // Regular menu items
              <button
                onClick={() => handleNavigation(item.path)}
                className={`w-full flex items-center h-[48px] transition-colors overflow-hidden ${isActive(item.path)
                  ? 'bg-[var(--bg-brand-layer-2)] text-[var(--forbrand-primary)]'
                  : 'text-gray-700 hover:bg-gray-100'
                  } ${!isMobile && isCollapsed
                    ? 'justify-center'
                    : 'justify-start'
                  }`}
              >
                <item.icon
                  className={`w-[48px] h-[48px] flex-shrink-0 ${!isMobile && isCollapsed ? 'mx-auto' : ''
                    }`}
                  isSelected={isActive(item.path)}
                />
                {(isMobile || !isCollapsed) && (
                  <span className="text-sm font-medium text-left whitespace-nowrap overflow-hidden text-ellipsis">{item.label}</span>
                )}
              </button>
            )}
          </div>
        ))}
        {/* TO-do */}
        {/* {
          !isCollapsed && (
            <div className="px-2">
                <div className="flex flex-wrap gap-3 items-center justify-start text-[10px] text-[var(--forbrand-color)]">
                <a
                    onClick={() =>
                      setModalOpen({ ...modalOpen, consentModal: true })
                    }
                    className="underline cursor-pointer text-[10px]"
                  >
                    Consent Notice
                  </a>
                  <span className="hidden sm:inline">|</span>
                  <a
                    onClick={() =>
                      setModalOpen({ ...modalOpen, privacyModal: true })
                    }
                    className="underline cursor-pointer text-[10px]"
                  >
                    Privacy Policy
                  </a>
                  <span className="hidden sm:inline">|</span>
                     <a
                    onClick={() =>
                      setModalOpen({ ...modalOpen, termsOfUse: true })
                    }
                    className="underline cursor-pointer text-[10px]"
                  >
                    Terms of Use
                  </a>
                </div>
              
            </div>
          )
        } */}
      </nav>
    </div>
  )

  return (
    <>
      {/* Desktop Sidebar */}
      <div
        className={`hidden lg:flex flex-col bg-white border-r border-gray-200 transition-all duration-300 flex-shrink-0 ${isCollapsed ? 'w-[65px]' : 'w-[300px]'
          }`}
      >
        {getSidebarContent(false)}

      </div>

      {/* Mobile Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-[300px] bg-white border-r border-gray-200 transform transition-transform duration-300 lg:hidden ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
      >
        {getSidebarContent(true)}
      </div>
    </>
  )
}

export default Sidebar
